<script setup lang="ts">
import IconCart from '~icons/mdi/cart'
import IconHeart from '~icons/mdi/heart'
import IconHistory from '~icons/mdi/history'
import IconLogout from '~icons/mdi/logout'
import IconMenu from '~icons/mdi/menu'

const constants = useConstants()
const userStore = useUserStore()
const cartStore = useCart()
const categoryGroupStore = useCategoryGroup()
const categoryGroupReverseI18n = useCategoryI18nReverse('enum.product_category_group.')

const localePath = useLocalePath()

const mobileMenuOpen = ref(false)

interface MenuItem { title?: string, to?: string, i18nKey?: string, prependIcon?: any, class?: string, onClick?: (() => unknown), activeQueryParam?: string }
const menuItems = ref<MenuItem[]>([])

function clickOnMenuItem(item: MenuItem) {
  mobileMenuOpen.value = false
  if (item.onClick)
    item.onClick()
}

watch([() => userStore.data, () => categoryGroupStore.categoryGroup, () => categoryGroupStore.isCategoryGroupsPending], () => {
  menuItems.value = []

  // Clinic Chain Member
  if (userStore.data?.clinicChain) {
    menuItems.value.push({
      title: userStore.data.clinicChain.name,
      to: localePath('/clinic-chain'),
      activeQueryParam: constants.QUERY_PARAMS.categories,
    })
  }

  if (!categoryGroupStore.isCategoryGroupsPending && categoryGroupStore.categoryGroup.length > 0) {
    const sortedCategoryGroup = [...categoryGroupStore.categoryGroup].sort(sortByHandle(['pharma', 'practice_supply', 'food']))

    sortedCategoryGroup.forEach((category) => {
      menuItems.value.push({
        i18nKey: category.i18nKey,
        to: localePath(`/${categoryGroupReverseI18n.translateNormalized(category.handle)}`),
      })
    })
  }

  menuItems.value.push({
    to: localePath('/suppliers'),
    i18nKey: 'layout.menuSecondary.suppliers',
  })

  menuItems.value.push({
    to: localePath('/campaigns'),
    i18nKey: 'layout.menuSecondary.campaigns',
  })

  menuItems.value.push({
    i18nKey: 'logout',
    prependIcon: IconLogout,
    onClick: () => { userStore.logout() },
  })
}, { immediate: true })
</script>

<template>
  <v-bottom-navigation
    bg-color="red-400"
    elevation="0"
    grow
    class="w-100 mobile-menu-wrapper"
  >
    <v-bottom-sheet v-model="mobileMenuOpen">
      <template #activator="{ props }">
        <v-btn v-bind="props" class="pa-0">
          <v-icon :icon="IconMenu" />
          {{ $t('menu') }}
        </v-btn>
      </template>
      <v-list>
        <v-list-item
          v-for="(item, index) in menuItems"
          :key="index"
          :title="item.i18nKey ? $t(item.i18nKey) : item.title"
          :to="item.to ? item.to : undefined"
          :prepend-icon="item.prependIcon ? item.prependIcon : undefined"
          :class="item.class ? item.class : undefined"
          @click="clickOnMenuItem(item)"
        />
      </v-list>
    </v-bottom-sheet>
    <v-btn :to="localePath('/order-history')" class="pa-0">
      <v-icon :icon="IconHistory" />
      {{ $t('orders') }}
    </v-btn>

    <v-btn :to="localePath('/product-favorites')" class="pa-0">
      <v-icon :icon="IconHeart" />
      {{ $t('favorites') }}
    </v-btn>
    <v-btn v-if="cartStore.totalAmount" :to="localePath('/cart')" class="pa-0">
      <v-badge
        :max="99"
        :content="cartStore.totalAmount"
        offset-x="-5"
        color="heiland-light-blue"
      >
        <v-icon :icon="IconCart" />
      </v-badge>
      {{ $t('cart.title') }}
    </v-btn>
    <v-btn v-else color="white" :to="localePath('/cart')">
      <v-icon :icon="IconCart" />
      {{ $t('cart.title') }}
    </v-btn>
  </v-bottom-navigation>
</template>
